// eslint-disable-next-line no-use-before-define
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useRouteMatch, useLocation } from 'react-router-dom';
import OuterContainer from '../PatientSummary/OuterContainer';
import DocumentsList, { normaliseDocuments } from './DocumentsList';
import DocumentView from '../PatientSummary/Docs';
import { ROPatientContextProvider } from '../PatientSummary/context';
import { GET_PATIENT_DOCUMENTS } from '../PatientSummary/PatientProfileQueries';
import { useWindowSize } from 'shared-components/utils/CustomHooks';
import { LoadingSpinner } from 'shared-components/components';
import useInterval from 'shared-components/utils/useInterval';
import { LARGE_DISPLAY_SIZE } from 'shared-components/enums';
import { Box, Grid, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface DocumentsPageRouteProps {
  id: string;
  page: string;
}

interface DocumentsPageLocationState {
  openDocumentId?: string;
  openInEditMode?: boolean;
}

export const DocumentsPage = (): JSX.Element => {
  const match = useRouteMatch<DocumentsPageRouteProps>();
  const { id: patientId } = match.params;
  const location = useLocation<DocumentsPageLocationState>();
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const windowSize = useWindowSize();
  const isSmallDevice = windowSize.width < LARGE_DISPLAY_SIZE;
  const xsDevice = windowSize.width < 1490;
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentEditModes, setDocumentEditModes] = useState<Record<string, boolean>>({});
  const [pendingGroupIds, setPendingGroupIds] = useState<Set<string>>(new Set());
  const editIntentAppliedRef = useRef(false);

  const setEditMode = (docId: string, isEditing: boolean) =>
    setDocumentEditModes((prev) => ({ ...prev, [docId]: isEditing }));

  const showUnsubmittedChanges =
    selectedDocument?.status?.toLowerCase() === 'approved' &&
    (selectedDocument?.id in documentEditModes
      ? documentEditModes[selectedDocument.id]
      : selectedDocument?.hasUncommittedChanges ?? false);

  const { data, loading, error, refetch } = useQuery(GET_PATIENT_DOCUMENTS, {
    variables: { patientId: patientId },
    skip: !patientId,
    fetchPolicy: 'network-only',
  });
  const documents = useMemo(() => normaliseDocuments(data?.documents ?? []), [data]);

  // immediately refetch the document list after document approval and
  // record the group ID so we can automatically select the new version.
  const handleDocumentSubmitted = () => {
    if (selectedDocument?.editableDocumentGroupId) {
      setPendingGroupIds((prev) => new Set(prev).add(selectedDocument.editableDocumentGroupId));
    }
    refetch();
  };

  // Auto-select the new document version in the list after approval.
  // Also syncs selectedDocument with the latest data from the list so the viewer
  // reflects status changes (e.g. Loading → Approved) picked up by the list refetch.
  useEffect(() => {
    const currentGroupId = selectedDocument?.editableDocumentGroupId;

    if (currentGroupId && pendingGroupIds.has(currentGroupId)) {
      const newerVersion = documents.find(
        (doc: any) =>
          doc.editableDocumentGroupId === currentGroupId &&
          doc.id !== selectedDocument.id &&
          new Date(doc.dateOfDocument) > new Date(selectedDocument.dateOfDocument),
      );
      if (newerVersion) {
        setSelectedDocument(newerVersion);
        setPendingGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(currentGroupId);
          return next;
        });
        return;
      }
    }

    // Sync the selected document with the newest data from the list so the
    // viewer doesn't show stale status/URL after a background refetch.
    if (selectedDocument) {
      const updatedDoc = documents.find((doc: any) => doc.id === selectedDocument.id);
      if (updatedDoc && updatedDoc.status !== selectedDocument.status) {
        setSelectedDocument(updatedDoc);
      }
    }
  }, [documents, pendingGroupIds, selectedDocument]);

  // Apply edit intent from location.state
  useEffect(() => {
    const { openDocumentId, openInEditMode } = location.state ?? {};
    if (!openDocumentId || editIntentAppliedRef.current || !documents.length) return;
    const targetDoc = documents.find((doc: any) => doc.id === openDocumentId);
    if (targetDoc) {
      editIntentAppliedRef.current = true;
      setSelectedDocument(targetDoc);
      if (openInEditMode) {
        setEditMode(targetDoc.id, true);
      }
    }
  }, [documents]);

  /* Refresh documents list every 15 seconds */
  const refetchInterval = 15000;
  useInterval(() => {
    refetch();
  }, refetchInterval);

  useEffect(() => {
    if (xsDevice) {
      setShowDocumentModal(selectedDocument !== null);
    } else {
      setShowDocumentModal(false);
    }
  }, [xsDevice, selectedDocument]);
  if (error) return <div>Error</div>;
  return (
    <>
      <ROPatientContextProvider>
        <OuterContainer>
          <Grid container sx={{ padding: '0 16px', height: '100%' }}>
            <Grid
              item
              xs={isSmallDevice ? (xsDevice ? 12 : 4) : 3}
              sx={{
                padding: '16px',
                borderRadius: '4px',
                boxShadow: '0px 2px 8px rgba(113, 110, 106, 0.4)',
                backgroundColor: 'white',
                height: '100%',
              }}>
              {loading ? (
                <LoadingSpinner loadingText={'Loading Documents'} relativeSpinner={true} />
              ) : (
                <DocumentsList
                  documents={documents}
                  selectedDocument={selectedDocument}
                  setSelectedDocument={setSelectedDocument}
                  emptyStateMessage="No documents available"
                  documentEditModes={documentEditModes}
                  // On small screens the viewer is a modal the user opens/closes
                  // explicitly, so don't auto-select the first document (it would
                  // re-open the modal on the first doc the moment 'X' is clicked).
                  autoSelectFirst={!xsDevice}
                />
              )}
            </Grid>
            {xsDevice ? (
              <Dialog
                fullWidth
                sx={{ '& .MuiPaper-root': { minWidth: '90%', height: '100%' } }}
                open={showDocumentModal}
                onClose={() => {
                  setShowDocumentModal(false);
                  setSelectedDocument(null);
                }}
                id="document-modal">
                <DialogTitle sx={{ display: 'flex' }}>
                  <IconButton
                    sx={{ marginLeft: 'auto' }}
                    className="btn-close"
                    onClick={() => {
                      setShowDocumentModal(false);
                      setSelectedDocument(null);
                    }}>
                    <CloseIcon color="primary" />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ width: '100%' }}>
                  <DocumentView
                    document={selectedDocument}
                    isEditMode={documentEditModes[selectedDocument?.id] ?? false}
                    showUnsubmittedChanges={showUnsubmittedChanges}
                    setEditMode={setEditMode}
                    onDocumentSubmitted={handleDocumentSubmitted}
                  />
                </DialogContent>
              </Dialog>
            ) : (
              <Grid item xs={isSmallDevice ? 8 : 9} sx={{ paddingLeft: '16px', height: '100%' }}>
                {/* This extra element is needed to make shadows on this page visually consistent. 
                A better fix will be refactoring the grid layout for this page.   */}
                <Box
                  sx={{
                    backgroundColor: 'white',
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: '4px',
                    boxShadow: '0px 2px 8px rgba(113, 110, 106, 0.4)',
                  }}>
                  <DocumentView
                    document={selectedDocument}
                    isEditMode={documentEditModes[selectedDocument?.id] ?? false}
                    showUnsubmittedChanges={showUnsubmittedChanges}
                    setEditMode={setEditMode}
                    onDocumentSubmitted={handleDocumentSubmitted}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </OuterContainer>
      </ROPatientContextProvider>
    </>
  );
};
